import {
  Button,
  Form,
  FormTitle,
  Input,
  InputNumber,
  message,
  PopConfirm,
  Select,
  SubContent,
  Table,
  Tag,
} from '@maxtropy/components';
import { PlusOutlined } from '@ant-design/icons';
import { Col, Row, Space, Spin } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  apiV2CircuitListPost,
  apiV2ComprehensiveLabelPagePost,
  apiV2ElectricMapConfigOverviewDetailPost,
  apiV2ElectricMapConfigOverviewSaveAreaMapPost,
  apiV2ElectricMapConfigOverviewSaveBasicPost,
  apiV2ElectricMapConfigOverviewSaveEnergyPost,
  apiV2EnergyAnalysisListByOuPost,
  V2ElectricMapConfigOverviewDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import UploadImport from '@/components/UploadImport';
import { LogType } from '@/components/ImportLog/Prop';
import BindUnitModal, { AnalysisUnit } from '../BindUnitModal';
import HeighMoudal from '../HeighMoudal';
import Standmodal from '../StandModal';
import AreaMapTable, { AreaMapProp, AreaMapTableRef } from '../AreaMapTable';
import styles from './index.module.scss';

type MapDetailProp = NonNullable<V2ElectricMapConfigOverviewDetailPostResponse>;
export type HighColumnsProp = Exclude<MapDetailProp['highConsumptionList'], undefined>[number];
type CircuitProp = Exclude<MapDetailProp['circuitList'], undefined>[number];
type OverviewProp = MapDetailProp['overview'];

interface Option {
  label?: number | string;
  value?: number | string;
}

const processCircuitList = (originalCircuitList: CircuitProp[]) => {
  const totalItems = 3;
  let processed: any = [];

  if (originalCircuitList && originalCircuitList.length > 0) {
    processed = [...originalCircuitList]
      .slice(0, totalItems)
      .sort((a, b) => (a.index || 0) - (b.index || 0))
      .map((item, idx) => ({
        id: item.id,
        circuitId: item.circuitId,
        index: item.index || idx + 1,
      }));
  }

  if (processed.length < totalItems) {
    for (let i = processed.length; i < totalItems; i++) {
      processed.push({
        id: undefined,
        circuitId: undefined,
        index: i + 1,
      });
    }
  }

  return processed;
};

const ElectricMapConfig = () => {
  const [form] = Form.useForm();
  const [heighMoudalOpen, setHeighMoudalOpen] = useState<boolean>(false);
  const [standRoomOpen, setStandRoomOpen] = useState<boolean>(false);
  const [circuitIdOp, setCircuitIdOp] = useState<Option[]>([]);
  const [enterlineOp, setEnterlineOp] = useState<Option[]>([]);
  const [deviceList, setDeviceList] = useState<HighColumnsProp['deviceList']>();
  const [standRoomList, setStandRoomList] = useState<HighColumnsProp['roomList']>();
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [standRowId, setStandRowId] = useState<number | null>(null);
  const [basicEditing, setBasicEditing] = useState<boolean>(false);
  const [areaMapEditing, setAreaMapEditing] = useState<boolean>(false);
  const [energyEditing, setEnergyEditing] = useState<boolean>(false);
  const [showEnergyUnitModal, setShowEnergyUnitModal] = useState<boolean>(false);
  const [showWorkshopLightModal, setShowWorkshopLightModal] = useState<boolean>(false);
  const [hightTable, setHightTable] = useState<any[]>([]);
  const [areaTable, setAreaTable] = useState<AreaMapProp[]>([]);
  const [overview, setOverview] = useState<OverviewProp>();
  const [datas, setDatas] = useState<MapDetailProp>();
  const tableRef = useRef<AreaMapTableRef>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [childLoadingCount, setChildLoadingCount] = useState<number>(0);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [openGgdImportModal, setOpenGgdImportModa] = useState<boolean>(false);
  const [openStationImportModal, setOpenStationImportModal] = useState<boolean>(false);
  const [deviceLabels, setDeviceLabels] = useState<any[]>([]);
  const [unitGroup, setUnitGroup] = useState<AnalysisUnit[]>([]);
  const [areaSearchKeyword, setAreaSearchKeyword] = useState<string>('');

  const handleChildLoadingChange = useCallback((loading: boolean) => {
    setChildLoadingCount(count => Math.max(loading ? count + 1 : count - 1, 0));
  }, []);

  useEffect(() => {
    setLoading(true);
    apiV2ElectricMapConfigOverviewDetailPost({})
      .then(res => {
        const processedCircuitList = processCircuitList(res?.circuitList || []);
        const areaMapList = (res?.areaMapList ?? []).map(item => ({
          ...item,
          type: (item as { type?: number }).type ?? 2,
          pointLng: item.pointLng,
          pointLat: item.pointLat,
          displayWindow: item.displayWindow ?? false,
        }));

        setAreaTable(areaMapList.sort((a, b) => a.id! - b.id!) || []);
        setHightTable(res.highConsumptionList || []);
        setOverview(res.overview);
        setDatas(res);

        form.setFieldsValue({
          areaMapList,
          highConsumptionList: res.highConsumptionList || [],
          overview: {
            ...res.overview,
            circuitId: res?.pvCircuitIds ?? [],
            deviceLabel: res?.labelIds || [],
          },
          circuitList: processedCircuitList,
          deviceLabel: res?.labelIds || [],
          energyGroupId: res.energyGroupId,
          circuitEnergyGroupId: res.circuitEnergyGroupId,
          circuitEnergyUnitId: res.circuitEnergyUnitId,
          circuitEnergyUnitName: res.circuitEnergyUnitName,
          voltage: res.voltage,
        });
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [form, refresh]);

  useEffect(() => {
    Promise.all([
      apiV2CircuitListPost({}).then(res => {
        setCircuitIdOp((res.list ?? []).map(i => ({ label: i.name, value: i.id })));
      }),
      apiV2CircuitListPost({ type: 1 }).then(res => {
        setEnterlineOp((res.list ?? []).map(i => ({ label: i.name, value: i.id })));
      }),
      apiV2ComprehensiveLabelPagePost({
        page: 1,
        size: 100,
        type: 3,
      }).then(res => {
        setDeviceLabels(res.list || []);
      }),
      apiV2EnergyAnalysisListByOuPost({}).then(res => {
        setUnitGroup((res.list ?? []) as AnalysisUnit[]);
      }),
    ])
      .catch(() => undefined)
      .finally(() => setOptionsLoading(false));
  }, []);

  const buildEnergyPayload = (values: any) => {
    const formattedCircuits = values?.circuitList.map((item: any, index: number) => {
      const valueItem = values?.circuitList?.[index] || {};
      return {
        circuitId: valueItem.circuitId,
        index: item.index ?? index + 1,
        id: item.id ?? undefined,
      };
    });

    const formattedHighConsump = (values.highConsumptionList ?? []).map((item: any) => {
      const id = item.new ? undefined : item.id;
      return {
        ...item,
        id,
      };
    });

    const formattedOverview = {
      installedCapacity: values.overview?.installedCapacity,
      accountId: values.overview?.accountId ?? overview?.accountId,
      id: overview?.id || undefined,
    };

    return {
      circuitList: formattedCircuits,
      pvCircuitIds: values.overview?.circuitId ?? [],
      overview: formattedOverview,
      highConsumptionList: formattedHighConsump,
    };
  };

  const buildBasicPayload = (values: any) => {
    return {
      labelIds: values.deviceLabel || [],
      energyGroupId: values.energyGroupId,
      circuitEnergyGroupId: values.circuitEnergyGroupId,
      circuitEnergyUnitId: values.circuitEnergyUnitId,
      voltage: values.voltage,
      periodList: datas?.periodList ?? [],
      monitorList: datas?.monitorList ?? [],
    };
  };

  const saveBasicConfig = async () => {
    if (!basicEditing) {
      setBasicEditing(true);
      return;
    }

    await form.validateFields([
      ['overview', 'installedCapacity'],
      ['overview', 'circuitId'],
      ['circuitList', 0, 'circuitId'],
      ['circuitList', 1, 'circuitId'],
      ['circuitList', 2, 'circuitId'],
      'highConsumptionList',
    ]);
    const values = form.getFieldsValue(true);

    setSaveLoading(true);
    apiV2ElectricMapConfigOverviewSaveEnergyPost(buildEnergyPayload(values))
      .then(() => {
        message.success('保存成功！');
        setBasicEditing(false);
        setRefresh(prev => !prev);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const saveAreaMapConfig = async () => {
    if (!areaMapEditing) {
      setAreaMapEditing(true);
      return;
    }

    const allData = tableRef.current?.getAllData() ?? [];
    setSaveLoading(true);
    apiV2ElectricMapConfigOverviewSaveAreaMapPost({
      areaMapList: allData,
    })
      .then(() => {
        message.success('保存成功！');
        setAreaMapEditing(false);
        setRefresh(prev => !prev);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const saveEnergyConfig = async () => {
    if (!energyEditing) {
      setEnergyEditing(true);
      return;
    }

    await form.validateFields([
      'deviceLabel',
      'energyGroupId',
      'circuitEnergyGroupId',
      'circuitEnergyUnitId',
      'voltage',
    ]);
    const values = form.getFieldsValue(true);

    setSaveLoading(true);
    apiV2ElectricMapConfigOverviewSaveBasicPost(buildBasicPayload(values))
      .then(() => {
        message.success('保存成功！');
        setEnergyEditing(false);
        setRefresh(prev => !prev);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const cancelSectionEdit = (setEditing: (editing: boolean) => void) => {
    setEditing(false);
    setRefresh(prev => !prev);
  };

  const renderSectionEditButton = (
    editing: boolean,
    onClick: () => void,
    onCancel: () => void,
    showDivider = false
  ) => (
    <>
      <div className={styles.sectionEditAction}>
        <Space size={8}>
          <Button type="primary" loading={saveLoading} onClick={onClick}>
            {editing ? '保存' : '编辑'}
          </Button>
          {editing && <Button onClick={onCancel}>取消</Button>}
        </Space>
      </div>
      {showDivider && <div className={styles.sectionDivider} />}
    </>
  );

  const handleModalSave = (update: HighColumnsProp['deviceList']) => {
    if (editingRowId === null) return;
    const currentList = form.getFieldValue('highConsumptionList');
    const newHighList = currentList?.map((row: HighColumnsProp) =>
      row.id === editingRowId ? { ...row, deviceList: update } : row
    );
    form.setFieldsValue({
      highConsumptionList: newHighList,
    });

    setEditingRowId(null);
    setHeighMoudalOpen(false);
  };

  const handleTreeSelect = (info: any) => {
    addrow(info);
  };

  const addrow = (info: any) => {
    const hightTable = form.getFieldValue('highConsumptionList');
    const newRow = {
      id: uuidv4(),
      new: true,
      energyUnitId: info.key,
      energyUnitName: info.label,
      energyUnitGroupName: info.groupLabel,
      energyUnitGroupId: info.group,
    };
    const newData = [...(hightTable ?? []), newRow];
    setHightTable(newData);
    form.setFieldsValue({ highConsumptionList: newData });
  };

  const ModalStandRoomSave = (update: HighColumnsProp['roomList']) => {
    if (standRowId === null) return;
    const currentList = form.getFieldValue('highConsumptionList');
    const newHighList = currentList?.map((row: HighColumnsProp) =>
      row.id === standRowId ? { ...row, roomList: update } : row
    );
    form.setFieldsValue({
      highConsumptionList: newHighList,
    });

    setStandRowId(null);
    setHeighMoudalOpen(false);
  };

  const highColumns = [
    {
      title: '区域（用能单元）',
      dataIndex: 'energyUnitName',
      width: 300,
    },
    {
      title: '用能分析组',
      dataIndex: 'energyUnitGroupName',
      width: 400,
    },
    {
      title: '供电站房',
      dataIndex: 'roomList',
      width: 120,
      render: (_: ReactNode, record: HighColumnsProp) => {
        const currentList = form.getFieldValue('highConsumptionList');
        return (
          <Button
            disabled={!basicEditing}
            type="link"
            onClick={() => {
              const newHighList = currentList?.find((row: HighColumnsProp) => row.id === record.id);
              setStandRoomList(newHighList?.roomList);
              setStandRowId(record.id ?? null);
              setStandRoomOpen(true);
            }}
          >
            编辑
          </Button>
        );
      },
    },
    {
      title: '高能耗点',
      dataIndex: 'deviceList',
      width: 120,
      render: (_: ReactNode, record: HighColumnsProp) => {
        const currentList = form.getFieldValue('highConsumptionList');
        return (
          <Button
            disabled={!basicEditing}
            type="link"
            onClick={() => {
              const newHighList = currentList?.find((row: HighColumnsProp) => row.id === record.id);
              setDeviceList(newHighList?.deviceList);
              setEditingRowId(record.id ?? null);
              setHeighMoudalOpen(true);
            }}
          >
            编辑
          </Button>
        );
      },
    },
    {
      title: '操作',
      dataIndex: 'option',
      width: 120,
      render: (_: any, __: HighColumnsProp, index: number) => {
        return (
          <PopConfirm
            title="您是否删除？"
            onConfirm={() => {
              const currentList: HighColumnsProp[] = form.getFieldValue('highConsumptionList') || [];
              const newList = currentList.filter((_, i) => i !== index);
              setHightTable(newList);
              form.setFieldsValue({ highConsumptionList: newList });
            }}
          >
            <Button disabled={!basicEditing} type="link">
              删除
            </Button>
          </PopConfirm>
        );
      },
    },
  ];

  return (
    <Spin
      spinning={loading || optionsLoading || saveLoading || childLoadingCount > 0}
      wrapperClassName={styles.pageSpin}
    >
      <Form form={form} layout="horizontal">
        <div style={{ padding: 20 }}>
          <FormTitle title="基础配置" />
          <SubContent title="规模与总览卡片">
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  label="光伏装机容量"
                  name={['overview', 'installedCapacity']}
                  rules={[{ required: true, message: '请选择回路' }]}
                >
                  <InputNumber
                    disabled={!basicEditing}
                    style={{ width: '472px' }}
                    min={1}
                    max={999}
                    precision={1}
                    placeholder="请输入"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="光伏回路"
                  name={['overview', 'circuitId']}
                  rules={[{ required: true, message: '请选择回路' }]}
                >
                  <Select
                    disabled={!basicEditing}
                    showSearch
                    mode="multiple"
                    optionFilterProp="label"
                    placeholder="请选择"
                    style={{ width: '100%' }}
                    options={circuitIdOp}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  label="淼船线"
                  name={['circuitList', 0, 'circuitId']}
                  rules={[{ required: true, message: '请选择回路' }]}
                >
                  <Select
                    disabled={!basicEditing}
                    showSearch
                    optionFilterProp="label"
                    placeholder="请选择"
                    style={{ width: '100%' }}
                    options={enterlineOp}
                  />
                </Form.Item>
                <Form.Item
                  label="东船线"
                  name={['circuitList', 1, 'circuitId']}
                  rules={[{ required: true, message: '请选择回路' }]}
                >
                  <Select
                    disabled={!basicEditing}
                    showSearch
                    optionFilterProp="label"
                    placeholder="请选择"
                    style={{ width: '100%' }}
                    options={enterlineOp}
                  />
                </Form.Item>
                <Form.Item
                  label="洲船线"
                  name={['circuitList', 2, 'circuitId']}
                  rules={[{ required: true, message: '请选择回路' }]}
                >
                  <Select
                    disabled={!basicEditing}
                    showSearch
                    optionFilterProp="label"
                    placeholder="请选择"
                    style={{ width: '100%' }}
                    options={enterlineOp}
                  />
                </Form.Item>
              </Col>
            </Row>
          </SubContent>
          <SubContent title="高能耗区域负荷与电量的用能单元">
            <Row gutter={[8, 5]}>
              <Col span={24}>
                <Button
                  type="primary"
                  disabled={!basicEditing}
                  //@ts-ignore
                  icon={<PlusOutlined />}
                  onClick={() => setShowEnergyUnitModal(true)}
                >
                  新建
                </Button>
                <Form.Item noStyle name="highConsumptionList">
                  <Table rowKey="id" loading={loading} columns={highColumns} dataSource={hightTable} />
                </Form.Item>
              </Col>
            </Row>
          </SubContent>
          {renderSectionEditButton(basicEditing, saveBasicConfig, () => cancelSectionEdit(setBasicEditing), true)}
          <FormTitle title="区域映射表" />
          <SubContent
            extraContent={
              <Input
                placeholder="请输入区域名称"
                value={areaSearchKeyword}
                onChange={e => setAreaSearchKeyword(e.target.value)}
                style={{ width: 200 }}
                allowClear
              />
            }
          >
            <Spin spinning={loading}>
              <AreaMapTable
                ref={tableRef}
                fullDataSource={areaTable}
                dataSource={areaTable.filter(item => item.zoneName?.includes(areaSearchKeyword))}
                isEditing={areaMapEditing}
                form={form}
                onLoadingChange={handleChildLoadingChange}
              />
            </Spin>
          </SubContent>

          {renderSectionEditButton(areaMapEditing, saveAreaMapConfig, () => cancelSectionEdit(setAreaMapEditing), true)}
          <FormTitle title="其他配置" />
          <SubContent title="报警统计配置">
            <Col span={12}>
              <Form.Item
                name="deviceLabel"
                label={<span style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>设备标签</span>}
                rules={[{ required: true, message: '请输入设备标签' }]}
              >
                <Select
                  placeholder="请选择设备标签"
                  options={deviceLabels.map(i => ({ label: i.name, value: i.id }))}
                  showSearch
                  optionFilterProp="label"
                  mode="multiple"
                  disabled={!energyEditing}
                  allowClear
                />
              </Form.Item>
            </Col>
          </SubContent>
          <SubContent title="高杆灯映射表">
            <Button type="link" onClick={() => setOpenGgdImportModa(true)}>
              配置映射表
            </Button>
          </SubContent>
          <SubContent title="站房映射表">
            <Button type="link" onClick={() => setOpenStationImportModal(true)}>
              配置映射表
            </Button>
          </SubContent>
          <SubContent title="用能流向">
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item name="energyGroupId" label="用能分析组" style={{ marginBottom: 0 }}>
                  <Select
                    optionFilterProp="label"
                    disabled={!energyEditing}
                    placeholder="请选择"
                    options={unitGroup.map(u => ({ label: u.name, value: u.id }))}
                    allowClear={true}
                    showSearch
                  />
                </Form.Item>
              </Col>
            </Row>
          </SubContent>
          <SubContent title="车间照明用电">
            <Form.Item name="circuitEnergyUnitId" hidden>
              <input type="hidden" />
            </Form.Item>
            <Form.Item name="circuitEnergyGroupId" hidden>
              <input type="hidden" />
            </Form.Item>
            <Button type="link" disabled={!energyEditing} onClick={() => setShowWorkshopLightModal(true)}>
              配置
            </Button>
            {form.getFieldValue('circuitEnergyUnitName') && (
              <Tag
                closable={energyEditing}
                onClose={() => {
                  form.setFieldsValue({
                    circuitEnergyUnitId: null,
                    circuitEnergyUnitName: null,
                    circuitEnergyGroupId: null,
                    circuitEnergyGroupName: null,
                  });
                }}
              >
                {form.getFieldValue('circuitEnergyUnitName')}
              </Tag>
            )}
          </SubContent>
          <SubContent title="太阳能路灯放电电压规格">
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item name="voltage" label="放电电压规格">
                  <InputNumber
                    disabled={!energyEditing}
                    style={{ width: '472px' }}
                    min={0}
                    precision={1}
                    placeholder="请输入"
                    addonAfter="V"
                  />
                </Form.Item>
              </Col>
            </Row>
            {renderSectionEditButton(energyEditing, saveEnergyConfig, () => cancelSectionEdit(setEnergyEditing))}
          </SubContent>
        </div>
      </Form>

      {showEnergyUnitModal && <BindUnitModal setOpen={setShowEnergyUnitModal} onUnitIdSelect={handleTreeSelect} />}

      {showWorkshopLightModal && (
        <BindUnitModal
          setOpen={setShowWorkshopLightModal}
          linkedId={form.getFieldValue('circuitEnergyUnitId')}
          linkedEnergyGroupId={form.getFieldValue('circuitEnergyGroupId')}
          onUnitIdSelect={nodeInfo => {
            form.setFieldsValue({
              circuitEnergyUnitId: nodeInfo.key ? Number(nodeInfo.key) : null,
              circuitEnergyUnitName: nodeInfo.label,
              circuitEnergyGroupId: nodeInfo.group,
              circuitEnergyGroupName: nodeInfo.groupLabel,
            });
            setShowWorkshopLightModal(false);
          }}
        />
      )}

      {heighMoudalOpen && (
        <HeighMoudal
          open={heighMoudalOpen}
          setOpen={setHeighMoudalOpen}
          deviceList={deviceList}
          onSave={handleModalSave}
        />
      )}
      {openGgdImportModal && <UploadImport title="高杆灯" type={LogType.GGD} setOpen={setOpenGgdImportModa} />}
      {openStationImportModal && (
        <UploadImport title="站房" type={LogType.STATION} setOpen={setOpenStationImportModal} />
      )}
      {standRoomOpen && (
        <Standmodal
          open={standRoomOpen}
          setOpen={setStandRoomOpen}
          standRoomData={standRoomList}
          onSave={ModalStandRoomSave}
        />
      )}
    </Spin>
  );
};

export default ElectricMapConfig;
